"use client";

import { useId, useRef, useState, type ReactNode } from "react";
import {
  Clock3,
  Eye,
  EyeOff,
  Loader2,
  LockKeyhole,
  ShieldCheck,
} from "lucide-react";
import { Button, Dialog, DialogActions, Field } from "../primitives/ui";
import "./privacy-reveal.css";

export type MaskedValueProps = {
  /** Display the value supplied by the server; this component never masks it again. */
  value: string | null | undefined;
  label?: string;
  revealedValue?: string | null;
  canReveal?: boolean;
  onReveal?: () => void;
  onHide?: () => void;
  busy?: boolean;
  error?: string;
  self?: boolean;
  fallback?: ReactNode;
};

export function MaskedValue({
  value,
  label = "개인정보",
  revealedValue,
  canReveal = false,
  onReveal,
  onHide,
  busy = false,
  error,
  self = false,
  fallback = "—",
}: MaskedValueProps) {
  const id = useId();
  const revealed =
    !self && canReveal && revealedValue !== null && revealedValue !== undefined;
  const displayed = revealed ? revealedValue : value;
  const revealAvailable =
    !self &&
    canReveal &&
    !!onReveal &&
    value !== null &&
    value !== undefined &&
    value !== "";
  return (
    <span className={`privacy-value${revealed ? " is-revealed" : ""}`}>
      <span className="privacy-value-line">
        <span
          className="privacy-value-text"
          aria-describedby={error ? `${id}-error` : undefined}
        >
          {displayed === null || displayed === undefined || displayed === ""
            ? fallback
            : displayed}
        </span>
        {revealed ? (
          <Button
            className="privacy-value-action"
            variant="ghost"
            size="sm"
            disabled={busy || !onHide}
            aria-label={`${label} 가리기`}
            onClick={onHide}
          >
            {busy ? (
              <Loader2 size={13} className="spin" aria-hidden="true" />
            ) : (
              <EyeOff size={13} aria-hidden="true" />
            )}
            가리기
          </Button>
        ) : revealAvailable ? (
          <Button
            className="privacy-value-action"
            variant="ghost"
            size="sm"
            disabled={busy}
            aria-label={`${label} 보기`}
            aria-busy={busy || undefined}
            onClick={onReveal}
          >
            {busy ? (
              <Loader2 size={13} className="spin" aria-hidden="true" />
            ) : (
              <Eye size={13} aria-hidden="true" />
            )}
            {busy ? "불러오는 중" : "보기"}
          </Button>
        ) : null}
      </span>
      {error && (
        <span id={`${id}-error`} className="privacy-value-error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}

export type RevealPasswordStatus =
  | "idle"
  | "checking"
  | "incorrect"
  | "rate-limited"
  | "forbidden";
export type RevealPasswordDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCloseAutoFocus?: (event: Event) => void;
  onConfirm: (password: string) => void | Promise<void>;
  status?: RevealPasswordStatus;
  error?: string;
  retryAfterSeconds?: number;
};
const passwordExplanation =
  "개인정보를 보려면 비밀번호를 다시 입력하세요. 원문을 볼 때마다 감사 기록에 남습니다.";

/** Closing unmounts the form, so passwords never survive reopening. Auth state belongs to the caller. */
export function RevealPasswordDialog(props: RevealPasswordDialogProps) {
  if (!props.open) return null;
  return <RevealPasswordForm {...props} />;
}

function RevealPasswordForm({
  open,
  onOpenChange,
  onCloseAutoFocus,
  onConfirm,
  status = "idle",
  error,
  retryAfterSeconds,
}: RevealPasswordDialogProps) {
  const formId = useId();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const submitted = useRef(false);
  const checking = status === "checking" || submitting;
  const blocked = status === "rate-limited" || status === "forbidden";
  const retryMinutes =
    retryAfterSeconds !== undefined && Number.isFinite(retryAfterSeconds)
      ? Math.max(1, Math.ceil(retryAfterSeconds / 60))
      : 10;
  const stateError =
    status === "incorrect"
      ? "비밀번호가 올바르지 않습니다. 다시 입력해 주세요."
      : status === "rate-limited"
        ? `시도가 너무 많습니다. ${retryMinutes}분 뒤 다시 시도해 주세요.`
        : status === "forbidden"
          ? "개인정보 원문을 볼 권한이 없습니다."
          : "";
  const message = error || submissionError || stateError;
  const close = () => {
    setPassword("");
    setSubmissionError("");
    onOpenChange(false);
  };
  const confirm = async () => {
    if (checking || blocked || submitted.current || !password) return;
    submitted.current = true;
    setSubmitting(true);
    setSubmissionError("");
    try {
      await onConfirm(password);
    } catch (cause) {
      setSubmissionError(
        cause instanceof Error ? cause.message : String(cause),
      );
    } finally {
      setPassword("");
      setSubmitting(false);
      submitted.current = false;
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      onCloseAutoFocus={onCloseAutoFocus}
      title="개인정보 보기"
      description={passwordExplanation}
      footer={
        <DialogActions
          secondary={{ label: "취소", onClick: close }}
          primary={{
            label: checking ? (
              <>
                <Loader2 size={15} className="spin" aria-hidden="true" />
                확인 중
              </>
            ) : (
              "확인"
            ),
            type: "submit",
            form: formId,
            disabled: checking || blocked || !password,
            "aria-busy": checking || undefined,
          }}
        />
      }
    >
      <form
        id={formId}
        className="privacy-password-form"
        onSubmit={(event) => {
          event.preventDefault();
          void confirm();
        }}
      >
        <div className="privacy-password-intro">
          <span className="privacy-password-icon">
            <LockKeyhole size={22} aria-hidden="true" />
          </span>
          <p>{passwordExplanation}</p>
        </div>
        <Field
          label="비밀번호"
          type="password"
          autoComplete="current-password"
          autoFocus
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
            setSubmissionError("");
          }}
          placeholder="현재 로그인 비밀번호"
          disabled={checking || blocked}
          error={message || undefined}
        />
        <p className="privacy-password-note">
          <Clock3 size={14} aria-hidden="true" />
          확인하면 10분 동안 선택한 항목의 원문을 볼 수 있습니다.
        </p>
      </form>
    </Dialog>
  );
}

export type RevealGrantStatusProps = {
  remainingSeconds: number;
  onEnd: () => void;
  busy?: boolean;
  error?: string;
};

/** No timer or live region: the caller supplies remaining time and revokes the grant. */
export function RevealGrantStatus({
  remainingSeconds,
  onEnd,
  busy = false,
  error,
}: RevealGrantStatusProps) {
  if (!Number.isFinite(remainingSeconds) || remainingSeconds <= 0) return null;
  const seconds = Math.ceil(remainingSeconds);
  const remaining = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
  return (
    <div className="privacy-grant" aria-label="개인정보 보기 허가 상태">
      <div className="privacy-grant-line">
        <ShieldCheck size={16} aria-hidden="true" />
        <strong>개인정보 보기 허가 중</strong>
        <span className="privacy-grant-time" aria-live="off">
          {remaining} 남음
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onEnd}
          disabled={busy}
          aria-busy={busy || undefined}
          className="privacy-grant-end"
        >
          {busy && <Loader2 size={13} className="spin" aria-hidden="true" />}
          {busy ? "끝내는 중" : "끝내기"}
        </Button>
      </div>
      {error && (
        <p className="privacy-grant-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
